import { toast } from 'sonner'

import { FormEvent, useState } from 'react'

import { Card, CardContent, CardHeader, CardTitle, Input, PosButton, Segmented } from '@/components/ui'
import { useRepayCredit } from '@/hooks/features/customers'
import { usePrinter } from '@/services/printer'
import { PosModel } from '@/types'
import { formatMoney } from '@/utils'

import { CustomerDialog } from './CustomerDialog'

type Method = 'cash' | 'card' | 'bank' | 'qr'

/** A customer paying back what they owe on credit. Counts toward the shift's cash when paid in cash. */
export const CreditRepaymentCard = () => {
  const [customer, setCustomer] = useState<PosModel.Customer>()
  const [picking, setPicking] = useState(false)
  const [method, setMethod] = useState<Method>('cash')
  const repay = useRepayCredit()
  const { printSlip } = usePrinter()

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!customer) return setPicking(true)
    const form = e.currentTarget
    const data = new FormData(form)
    const amount = parseFloat(String(data.get('amount') ?? '')) || 0
    repay.mutate(
      { customerId: customer._id, amount, method, remark: String(data.get('remark') ?? '').trim() || undefined },
      {
        onSuccess: (slip) => {
          toast.success(`${formatMoney(amount)} received from ${customer.name}`)
          printSlip({
            title: 'CREDIT REPAYMENT',
            ref: slip.receipt_no,
            created_at: slip.created_at,
            rows: [
              ['Customer', slip.customer],
              ['Method', method],
              ['Received', amount.toFixed(2)],
            ],
          })
          form.reset()
          setCustomer(undefined)
        },
        onError: (err) => toast.error(err.message),
      }
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Credit repayment</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-3">
          <PosButton type="button" variant="outline" size="touch" className="w-full justify-between" onClick={() => setPicking(true)}>
            <span>{customer ? customer.name : 'Choose customer'}</span>
            {customer && <span className="text-muted-foreground">owes {formatMoney(customer.credit_used ?? 0)}</span>}
          </PosButton>
          <Segmented<Method>
            value={method}
            onChange={setMethod}
            options={[
              { value: 'cash', label: 'Cash' },
              { value: 'card', label: 'Card' },
              { value: 'bank', label: 'Bank' },
              { value: 'qr', label: 'QR' },
            ]}
          />
          <Input name="amount" inputMode="decimal" placeholder="Amount" required className="h-11 text-right tabular-nums" />
          <Input name="remark" placeholder="Note (optional)" className="h-11" />
          <PosButton type="submit" size="touch" variant="secondary" className="w-full" disabled={repay.isPending}>
            Receive payment
          </PosButton>
        </form>
      </CardContent>
      <CustomerDialog open={picking} onOpenChange={setPicking} selected={customer} onSelect={setCustomer} />
    </Card>
  )
}
